import type { Conversation } from '@botpress/client'
import { Button } from '@bpinternal/ui-kit-next'
import { useInfiniteQuery, useQuery } from '@tanstack/react-query'
import { createFileRoute, Outlet, useNavigate, useParams } from '@tanstack/react-router'
import { useEffect, useRef } from 'react'
import { useMediaQuery } from 'usehooks-ts'
import { z } from 'zod'
import EmptyConversationDarkIcon from '~/assets/empty-conversation-dark.svg?react'
import EmptyConversationLightIcon from '~/assets/empty-conversation-light.svg?react'
import { BannerPortal, Boundary } from '~/components'
import { EmptyState } from '~/elements'
import {
  AiButton,
  ConversationHeader,
  ConversationsQueryParamBuilder,
  ConversationsTable,
  PublishAlert,
} from '~/features/conversations/components'
import '~/features/conversations/components/styles.css'
import { useArrowNavigation } from '~/features/conversations/hooks/useArrowNavigation'
import { useInsightsPlugin } from '~/features/conversations/hooks/useInsightsPlugin'
import { useInfiniteScroll } from '~/hooks'
import { conversationQueryOptions, listConversationsInfiniteQuery, messageQueryOptions } from '~/queries'
import { useLocalStore } from '~/stores'

const conversationsSearchSchema = z.object({
  conversationId: z
    .string()
    .optional()
    .catch(() => undefined),
  messageId: z
    .string()
    .optional()
    .catch(() => undefined),
  integrationName: z
    .string()
    .optional()
    .catch(() => undefined),
  participantIds: z
    .array(z.string())
    .optional()
    .catch(() => undefined),
  tags: z
    .record(z.string())
    .optional()
    .catch(() => undefined),
})

export const Route = createFileRoute('/workspaces/$workspaceId/bots/$botId/conversations')({
  validateSearch: conversationsSearchSchema,
  component: Component,
})

function Component() {
  return (
    <Boundary height={'200px'} loaderSize="6">
      <ConversationsPage />
    </Boundary>
  )
}

const ConversationsPage = () => {
  const { workspaceId, botId } = Route.useParams()
  const { conversationId } = useParams({ strict: false })
  const { conversationId: searchConversationId, messageId, integrationName, tags, participantIds } = Route.useSearch()
  const isMobile = useMediaQuery('(max-width: 768px)')
  const navigate = useNavigate()
  const isDarkTheme = useLocalStore((s) => s.getPreferredTheme()) === 'dark'
  const insightsPluginState = useInsightsPlugin(workspaceId, botId)
  const { insightsPlugin, isPublishRequired, defaultPluginInstall, autoUpdatePlugin } = insightsPluginState

  const {
    data: fetchedConversations,
    hasNextPage,
    fetchNextPage,
    isFetching,
  } = useInfiniteQuery(listConversationsInfiniteQuery({ botId, workspaceId, integrationName, tags, participantIds }))

  const bottomRef = useInfiniteScroll(fetchNextPage, hasNextPage)

  const { data: fetchedMessage } = useQuery({
    ...messageQueryOptions({ messageId: messageId ?? '', botId, workspaceId }),
    enabled: !!messageId,
  })

  const { data: fetchedConversation } = useQuery({
    ...conversationQueryOptions({
      botId,
      workspaceId,
      conversationId: searchConversationId ?? fetchedMessage?.message.conversationId ?? '',
    }),
    enabled: !!searchConversationId || !!fetchedMessage?.message.conversationId,
  })

  const conversations = fetchedConversation?.conversation
    ? [fetchedConversation.conversation]
    : fetchedConversations?.pages?.flatMap((page) => page.conversations) ?? []

  const selectedConversation: Conversation | undefined = conversations.find((conv) => conv.id === conversationId)

  const tableRef = useRef<HTMLDivElement>(null)
  const selectedConvRef = useRef<HTMLDivElement>(null)

  const isPluginInstallSent = useRef(false)
  useEffect(() => {
    if (!insightsPlugin && !isPluginInstallSent.current) {
      defaultPluginInstall()
      isPluginInstallSent.current = true
    } else if (!isPluginInstallSent.current) {
      // TODO: Handle updates differently
      // autoUpdatePlugin()
      // isPluginInstallSent.current = true
    }
  }, [])

  useEffect(() => {
    selectedConvRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
    })
  }, [selectedConversation])

  useArrowNavigation({
    onArrowPressed: (direction) => {
      let newSelectedConversation = undefined
      if (selectedConversation) {
        const currentIndex = conversations.findIndex((conv) => conv.id === selectedConversation.id)
        const newIndex =
          direction === 'up' ? (currentIndex - 1) % conversations.length : (currentIndex + 1) % conversations.length
        newSelectedConversation = conversations.at(newIndex)
      } else {
        newSelectedConversation = conversations.at(0)
      }
      if (newSelectedConversation) {
        navigate({
          to: '/workspaces/$workspaceId/bots/$botId/conversations/$conversationId',
          params: {
            conversationId: newSelectedConversation.id,
            workspaceId,
            botId,
          },
          search: (prev) => prev,
        })
      }
    },
  })

  if (isMobile && selectedConversation) {
    return (
      <div className="flex h-full flex-col gap-6">
        <Boundary loaderSize="5" className="h-full self-center">
          <ConversationHeader workspaceId={workspaceId} botId={botId} conversation={selectedConversation} />
          <Outlet />
        </Boundary>
      </div>
    )
  }

  return (
    <>
      {(!insightsPlugin || isPublishRequired) && (
        <BannerPortal>
          <PublishAlert botId={botId} />
        </BannerPortal>
      )}
      <div className="mx-auto flex max-w-(--breakpoint-2xl) md:mt-4 md:h-[calc(100vh-8rem)]">
        <div className="flex w-full flex-col gap-6 rounded-xl bg-neutral-100 md:max-w-[384px] md:min-w-[350px] md:px-4 md:py-6">
          <div className="flex justify-between">
            <h1 className="text-2xl font-semibold text-neutral-950 md:pb-0">Conversations</h1>
            <AiButton
              workspaceId={workspaceId}
              botId={botId}
              isMobile={isMobile}
              insightsPluginState={insightsPluginState}
            />
          </div>
          <ConversationsQueryParamBuilder workspaceId={workspaceId} botId={botId} />
          <Boundary loaderSize={'5'} className="h-full self-center">
            <div ref={tableRef} className="flex w-full flex-col items-center overflow-auto">
              <ConversationsTable
                selectedConvRef={selectedConvRef}
                conversations={conversations}
                workspaceId={workspaceId}
                botId={botId}
                selectedConversationId={selectedConversation?.id}
              />
              <div ref={bottomRef} />
              {hasNextPage && (
                <Button variant="ghost" disabled={isFetching} className="mt-2 w-fit" onClick={() => fetchNextPage()}>
                  Load more
                </Button>
              )}
            </div>
          </Boundary>
        </div>
        <div className="hidden min-w-0 flex-1 md:flex">
          {selectedConversation ? (
            <div className="flex h-full w-full min-w-[400px] flex-col gap-6 pt-6 pl-6">
              <Boundary loaderSize="5" className="h-full self-center">
                <ConversationHeader workspaceId={workspaceId} botId={botId} conversation={selectedConversation} />
                <div className="flex flex-1 flex-col gap-6 overflow-auto">
                  <Outlet />
                </div>
              </Boundary>
            </div>
          ) : (
            <EmptyState
              iconSize={6}
              icon={isDarkTheme ? EmptyConversationDarkIcon : EmptyConversationLightIcon}
              title="No Conversation Selected"
              className="flex w-full justify-center"
              description="Select a conversation from the list to view its messages"
            />
          )}
        </div>
      </div>
    </>
  )
}
